<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="save">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full xl:w-1/2 mx-auto md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ $t('Appearance') }}</h1>
                    </div>
                    <div class="mt-4 flex md:mt-0 md:ml-4">
                        <router-link class="btn-stage-secondary mr-3" to="/workspace/admin/settings">
                            {{ $t('Close') }}
                        </router-link>
                        <button class="btn-stage-primary" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div
                    v-for="section in sections"
                    :key="section.key"
                    class="w-full xl:w-1/2 mx-auto mt-6 first:mt-0 bg-white shadow sm:rounded"
                >
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t(section.title) }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">{{ $t(section.description) }}</div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2 space-y-3">
                            <div
                                v-for="asset in section.assets"
                                :key="asset.key"
                                class="appearance-asset-row"
                            >
                                <input
                                    :ref="asset.key"
                                    :accept="acceptString(asset.allowed)"
                                    class="hidden"
                                    type="file"
                                    @change="onFilePicked($event, asset)"
                                >

                                <div class="appearance-asset-main">
                                    <div :class="{'appearance-asset-preview-column--dark': asset.darkPreview}" class="appearance-asset-preview-column">
                                        <div :class="[previewClass(asset), {'appearance-asset-preview--dark': asset.darkPreview}]" class="appearance-asset-preview">
                                            <img :src="assetValue(asset.key).preview" :alt="$t(asset.label)">
                                        </div>
                                    </div>

                                    <div class="appearance-asset-info-column min-w-0">
                                        <div class="text-sm font-medium text-gray-900">{{ $t(asset.label) }}</div>
                                        <div class="mt-1 text-xs text-gray-500 leading-5">{{ $t(asset.hint) }}</div>
                                        <div class="mt-3 text-xs text-gray-400 leading-5">
                                            {{ $t('Allowed formats') }}: {{ asset.formats }}
                                        </div>
                                        <div class="mt-1 text-xs text-gray-400 leading-5">
                                            {{ $t('Recommended bitmap size') }}: {{ asset.bitmapSize }}
                                        </div>
                                    </div>
                                </div>

                                <div class="appearance-asset-footer">
                                    <button class="appearance-asset-upload-btn" type="button" @click="openPicker(asset.key)">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.arrow-up-from-line"></svg-vue>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="w-full xl:w-1/2 mx-auto mt-6 bg-white shadow sm:rounded">
                    <div class="p-6 md:grid md:grid-cols-3 md:gap-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Dialog backdrop') }}</h3>
                            <div class="mt-2 text-sm text-gray-500 leading-5">
                                {{ $t('Defines color, intensity and blur for the darkened surface behind system dialogs such as delete confirmations.') }}
                            </div>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
                                <input-color
                                    id="dialog_backdrop_color"
                                    v-model="dialog_backdrop_color"
                                    :label="$t('Color')"
                                    :placeholder="$t('Choose color')"
                                />

                                <div>
                                    <label class="stage-filter-label block mb-2" for="dialog_backdrop_alpha">{{ $t('Alpha (0 - 1)') }}</label>
                                    <input
                                        id="dialog_backdrop_alpha"
                                        v-model.number="dialog_backdrop_alpha"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        max="1"
                                        min="0"
                                        placeholder="0.75"
                                        step="0.01"
                                        type="number"
                                    >
                                </div>

                                <div>
                                    <label class="stage-filter-label block mb-2" for="dialog_backdrop_blur">{{ $t('Blur (0 - 40 px)') }}</label>
                                    <input
                                        id="dialog_backdrop_blur"
                                        v-model.number="dialog_backdrop_blur"
                                        class="stage-filter-input form-input block w-full text-sm transition duration-150 ease-in-out sm:leading-5"
                                        max="40"
                                        min="0"
                                        placeholder="0"
                                        step="1"
                                        type="number"
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
import {applyDialogBackdropSettings, normalizeDialogBackdropSettings} from '@/utils/dialog-backdrop';
import {UI_COLORS} from '@/utils/ui-colors';

export default {
    name: "appearance",
    metaInfo() {
        return {
            title: this.$i18n.t('Appearance')
        };
    },
    data() {
        return {
            loading: true,
            gui_logo: null,
            gui_logo_invert: null,
            gui_logo_toggle: null,
            gui_logo_toggle_invert: null,
            email_word_mark: null,
            email_design_mark: null,
            email_inbox_icon: null,
            favicon_svg: null,
            favicon_ico: null,
            favicon_16: null,
            favicon_32: null,
            favicon_apple_touch: null,
            app_background: null,
            dialog_backdrop_color: UI_COLORS.text.muted,
            dialog_backdrop_alpha: 0.75,
            dialog_backdrop_blur: 0,
            brandImageTypes: ['image/svg+xml', 'image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg', 'image/webp', 'image/gif', 'image/bmp'],
            bitmapTypes: ['image/png', 'image/x-citrix-png', 'image/x-png', 'image/jpeg', 'image/x-citrix-jpeg', 'image/webp', 'image/gif', 'image/bmp'],
            pngTypes: ['image/png', 'image/x-citrix-png', 'image/x-png'],
            svgTypes: ['image/svg+xml'],
            icoTypes: ['image/x-icon', 'image/vnd.microsoft.icon', 'application/vnd.microsoft.icon'],
        };
    },
    computed: {
        sections() {
            return [
                {
                    key: 'gui',
                    title: 'GUI branding',
                    description: 'Controls the main brand assets used in the application shell, including standard and inverted variants for bright and dark areas.',
                    assets: [
                        {
                            key: 'gui_logo',
                            label: 'Logo',
                            hint: 'Used in bright GUI areas such as public pages or light surfaces.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 640 x 160 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'wide',
                            darkPreview: false,
                        },
                        {
                            key: 'gui_logo_invert',
                            label: 'Logo invert',
                            hint: 'Used in dark GUI areas such as the sidebar and mobile header.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 640 x 160 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'wide',
                            darkPreview: true,
                        },
                        {
                            key: 'gui_logo_toggle',
                            label: 'Logo toggle',
                            hint: 'Compact brand mark for collapsed navigation on bright surfaces.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 256 x 256 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                        {
                            key: 'gui_logo_toggle_invert',
                            label: 'Logo toggle invert',
                            hint: 'Compact inverted brand mark for collapsed navigation on dark surfaces.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 256 x 256 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'square',
                            darkPreview: true,
                        },
                    ],
                },
                {
                    key: 'email',
                    title: 'E-mail branding',
                    description: 'Defines dedicated assets for email templates so word mark and design mark can be controlled independently.',
                    assets: [
                        {
                            key: 'email_word_mark',
                            label: 'Word mark',
                            hint: 'Primary logo asset for email headers.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 640 x 160 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'wide',
                            darkPreview: false,
                        },
                        {
                            key: 'email_design_mark',
                            label: 'Design mark',
                            hint: 'Secondary symbol asset for future email layouts and compact mail contexts.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 256 x 256 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                        {
                            key: 'email_inbox_icon',
                            label: 'Inbox icon',
                            hint: 'Square brand icon reserved for mailbox previews, sender-brand contexts and future BIMI-related integrations.',
                            formats: 'SVG, PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 256 x 256 px (retina-ready)',
                            allowed: this.brandImageTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                    ],
                },
                {
                    key: 'favicon',
                    title: 'Favicon assets',
                    description: 'Collects all favicon variants that browsers and devices commonly expect, including SVG, ICO, PNG sizes and Apple touch icon.',
                    assets: [
                        {
                            key: 'favicon_svg',
                            label: 'Favicon SVG',
                            hint: 'Best choice for modern browsers with crisp scaling.',
                            formats: 'SVG',
                            bitmapSize: 'not required',
                            allowed: this.svgTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                        {
                            key: 'favicon_ico',
                            label: 'Favicon ICO',
                            hint: 'Classic browser fallback for shortcut icons.',
                            formats: 'ICO',
                            bitmapSize: '16/32 px, ideally incl. 48 px for retina',
                            allowed: this.icoTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                        {
                            key: 'favicon_16',
                            label: 'Favicon 16x16',
                            hint: 'Bitmap fallback for browser tabs and bookmarks.',
                            formats: 'PNG',
                            bitmapSize: '32 x 32 px source for sharp 16 px rendering',
                            allowed: this.pngTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                        {
                            key: 'favicon_32',
                            label: 'Favicon 32x32',
                            hint: 'Bitmap fallback for higher density browser UIs.',
                            formats: 'PNG',
                            bitmapSize: '64 x 64 px source for sharp 32 px rendering',
                            allowed: this.pngTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                        {
                            key: 'favicon_apple_touch',
                            label: 'Apple touch icon',
                            hint: 'Used when websites are saved to the iOS home screen.',
                            formats: 'PNG, JPG, WEBP',
                            bitmapSize: '180 x 180 px (already retina-oriented)',
                            allowed: this.bitmapTypes,
                            preview: 'square',
                            darkPreview: false,
                        },
                    ],
                },
                {
                    key: 'background',
                    title: 'Login background',
                    description: 'Keeps the authentication background configurable as part of the overall appearance settings.',
                    assets: [
                        {
                            key: 'app_background',
                            label: 'Background',
                            hint: 'Used behind the login and authentication screens.',
                            formats: 'PNG, JPG, WEBP, GIF, BMP',
                            bitmapSize: 'min. 2560 x 1440 px for retina displays',
                            allowed: this.bitmapTypes,
                            preview: 'landscape',
                            darkPreview: false,
                        },
                    ],
                },
            ];
        },
        normalizedDialogBackdrop() {
            return normalizeDialogBackdropSettings({
                dialog_backdrop_color: this.dialog_backdrop_color,
                dialog_backdrop_alpha: this.dialog_backdrop_alpha,
                dialog_backdrop_blur: this.dialog_backdrop_blur,
            });
        },
    },
    mounted() {
        this.get();
    },
    methods: {
        normalizeAsset(value, fallbackPreview) {
            if (value && typeof value === 'object') {
                return {
                    file: null,
                    text: value.text || null,
                    preview: value.preview || fallbackPreview,
                };
            }

            return {
                file: null,
                text: null,
                preview: fallbackPreview,
            };
        },
        assetValue(key) {
            return this[key] || {file: null, text: null, preview: '/images/default/icon.png'};
        },
        acceptString(allowed) {
            return Array.isArray(allowed) ? allowed.join(',') : '';
        },
        previewClass(asset) {
            return {
                'appearance-asset-preview--wide': asset.preview === 'wide',
                'appearance-asset-preview--square': asset.preview === 'square',
                'appearance-asset-preview--landscape': asset.preview === 'landscape',
            };
        },
        openPicker(key) {
            const ref = this.$refs[key];
            const input = Array.isArray(ref) ? ref[0] : ref;
            if (input) {
                input.click();
            }
        },
        onFilePicked(event, asset) {
            const files = event && event.target ? event.target.files : null;
            if (!files || !files.length) {
                return;
            }

            const file = files[0];
            if (!asset.allowed.includes(file.type)) {
                this.$notify({
                    title: this.$i18n.t('Unsupported file type').toString(),
                    text: this.$i18n.t('Only image type files are accepted').toString(),
                    type: 'error'
                });
                return;
            }

            this[asset.key] = {
                ...this.assetValue(asset.key),
                file: file,
                preview: URL.createObjectURL(file),
            };
        },
        get() {
            const self = this;
            self.loading = true;

            return axios.get('api/workspace/admin/settings/appearance').then(function (response) {
                self.gui_logo = self.normalizeAsset(response.data.gui_logo, '/images/default/logo.svg');
                self.gui_logo_invert = self.normalizeAsset(response.data.gui_logo_invert || response.data.gui_logo, '/images/default/logo_invert.svg');
                self.gui_logo_toggle = self.normalizeAsset(response.data.gui_logo_toggle || response.data.app_icon, '/images/default/icon.png');
                self.gui_logo_toggle_invert = self.normalizeAsset(response.data.gui_logo_toggle_invert || response.data.gui_logo_toggle || response.data.app_icon, '/images/default/icon.png');
                self.email_word_mark = self.normalizeAsset(response.data.email_word_mark || response.data.gui_logo, '/images/default/logo.svg');
                self.email_design_mark = self.normalizeAsset(response.data.email_design_mark || response.data.gui_logo_toggle || response.data.app_icon, '/images/default/icon.png');
                self.email_inbox_icon = self.normalizeAsset(response.data.email_inbox_icon || response.data.email_design_mark || response.data.gui_logo_toggle || response.data.app_icon, '/images/default/icon.png');
                self.favicon_svg = self.normalizeAsset(response.data.favicon_svg, response.data.favicon_32?.preview || '/images/default/icon.png');
                self.favicon_ico = self.normalizeAsset(response.data.favicon_ico || response.data.app_icon, '/images/default/icon.png');
                self.favicon_16 = self.normalizeAsset(response.data.favicon_16 || response.data.app_icon, '/images/default/icon.png');
                self.favicon_32 = self.normalizeAsset(response.data.favicon_32 || response.data.app_icon, '/images/default/icon.png');
                self.favicon_apple_touch = self.normalizeAsset(response.data.favicon_apple_touch || response.data.app_icon, '/images/default/icon.png');
                self.app_background = self.normalizeAsset(response.data.app_background, '/images/default/background.jpg');
                const dialogBackdrop = normalizeDialogBackdropSettings(response.data || {});
                self.dialog_backdrop_color = dialogBackdrop.dialog_backdrop_color;
                self.dialog_backdrop_alpha = dialogBackdrop.dialog_backdrop_alpha;
                self.dialog_backdrop_blur = dialogBackdrop.dialog_backdrop_blur;
                self.syncWindowApp();
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/workspace/admin/settings');
            });
        },
        syncWindowApp() {
            const currentSettings = this.$store && this.$store.state && this.$store.state.settings
                ? this.$store.state.settings
                : {};
            const nextSettings = {
                ...currentSettings,
                logo: this.gui_logo.preview || '/images/default/logo.svg',
                logo_invert: this.gui_logo_invert.preview || this.gui_logo.preview || currentSettings.logo || '/images/default/logo.svg',
                logo_toggle: this.gui_logo_toggle.preview || '/images/default/icon.png',
                logo_toggle_invert: this.gui_logo_toggle_invert.preview || this.gui_logo_toggle.preview || currentSettings.logo_toggle || '/images/default/icon.png',
                icon: this.favicon_32.preview || this.favicon_16.preview || this.favicon_ico.preview || '/images/default/icon.png',
                background: this.app_background.preview || '/images/default/background.jpg',
                dialog_backdrop_color: this.normalizedDialogBackdrop.dialog_backdrop_color,
                dialog_backdrop_alpha: this.normalizedDialogBackdrop.dialog_backdrop_alpha,
                dialog_backdrop_blur: this.normalizedDialogBackdrop.dialog_backdrop_blur,
            };
            applyDialogBackdropSettings(nextSettings);
            this.$store.commit('patchSettings', nextSettings);

            const iconLinks = Array.from(document.querySelectorAll("link[rel='shortcut icon'], link[rel='icon']"));
            iconLinks.forEach((link) => {
                if (!link.getAttribute('sizes') && link.getAttribute('type') !== 'image/svg+xml') {
                    link.href = nextSettings.icon;
                }
            });
        },
        appendFile(formData, key) {
            const asset = this.assetValue(key);
            if (asset && asset.file) {
                formData.append(key === 'app_background' ? 'background' : key, asset.file);
            }
        },
        save() {
            const formData = new FormData();
            this.loading = true;

            [
                'gui_logo',
                'gui_logo_invert',
                'gui_logo_toggle',
                'gui_logo_toggle_invert',
                'email_word_mark',
                'email_design_mark',
                'email_inbox_icon',
                'favicon_svg',
                'favicon_ico',
                'favicon_16',
                'favicon_32',
                'favicon_apple_touch',
                'app_background',
            ].forEach((key) => {
                this.appendFile(formData, key);
            });

            formData.append('dialog_backdrop_color', this.normalizedDialogBackdrop.dialog_backdrop_color);
            formData.append('dialog_backdrop_alpha', String(this.normalizedDialogBackdrop.dialog_backdrop_alpha));
            formData.append('dialog_backdrop_blur', String(this.normalizedDialogBackdrop.dialog_backdrop_blur));

            axios.post('api/workspace/admin/settings/appearance', formData, {headers: {'Content-Type': 'multipart/form-data'}}).then(() => {
                return this.get();
            }).then(() => {
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t('Settings updated successfully').toString(),
                    type: 'success'
                });
            }).catch(() => {
                this.loading = false;
            });
        },
    },
}
</script>

<style lang="scss" scoped>
.appearance-asset-row {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--ui-border-default);
    border-radius: 0.375rem;
    overflow: hidden;
}

.appearance-asset-main {
    display: grid;
    grid-template-columns: 8rem minmax(0, 1fr);
    align-items: stretch;
}

.appearance-asset-preview-column {
    display: flex;
    align-items: stretch;
    justify-content: center;
    padding: 1rem;
    border-right: 1px solid var(--ui-border-default);
    background: #ffffff;
}

.appearance-asset-preview-column--dark {
    background: #000000;
}

.appearance-asset-info-column {
    padding: 1rem;
}

.appearance-asset-preview {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #ffffff;
}

.appearance-asset-preview img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.appearance-asset-preview--dark {
    background: #000000;
}

.appearance-asset-preview--wide {
    width: 6rem;
    height: 3rem;
    padding: 0.4rem;
}

.appearance-asset-preview--square {
    width: 4rem;
    height: 4rem;
    padding: 0.35rem;
}

.appearance-asset-preview--landscape {
    width: 6rem;
    height: 3.75rem;
    padding: 0.35rem;
}

.appearance-asset-footer {
    display: flex;
    justify-content: flex-end;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--ui-border-default);
    background: #ffffff;
}

.appearance-asset-upload-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: var(--ui-text-secondary);
    border: 0;
    padding: 0;
    cursor: pointer;
}

.appearance-asset-upload-btn:hover {
    color: var(--ui-text-heading);
}

</style>
